import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Stack } from "@mui/material";
import { toast } from "react-toastify";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import Breadcrumb from "components/Atoms/SharedComponents/Breadcrumbs/Breadcrumb";
import BookingDetails from "templates/events/bookings/details";
import {
  getBookingDetailsRequest,
  updateBookingStatusRequest,
} from "services/modules/events/bookings";

const BOOKING_STATUSES = [
  "confirmed",
  "in_preparation",
  "completed",
  "cancelled",
];

function BookingDetailsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { bookingId } = useParams();

  const details = useSelector((state) => state.bookings.booking);
  const load = useSelector((state) => state.bookings.load);

  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile,
  );
  const permissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions?.bookings,
  );
  const isSuperAdmin = currentUser?.privilege === "super_admin";
  const canUpdate = isSuperAdmin || permissions?.can_update;

  const [statusDraft, setStatusDraft] = useState("");

  const statusOptions = BOOKING_STATUSES.map((s) => ({
    id: s,
    name: t(`events_bookings.statuses.${s}`, { defaultValue: s }),
  }));

  const fetchDetails = () => {
    if (!bookingId) return;
    dispatch(
      getBookingDetailsRequest({
        id: bookingId,
        action: (res) => {
          setStatusDraft(res?.data?.booking?.booking_status || "");
        },
      }),
    );
  };

  const onStatusChange = (nextStatus) => {
    if (!bookingId || !nextStatus) return;
    dispatch(
      updateBookingStatusRequest({
        id: bookingId,
        body: { booking_status: nextStatus },
        action: () => {
          toast.success(t("events_bookings.status_updated"));
          fetchDetails();
        },
      }),
    );
  };

  useEffect(() => {
    fetchDetails();
  }, [bookingId]);

  useEffect(() => {
    if (details?.booking_status) {
      setStatusDraft(details.booking_status);
    }
  }, [details?.booking_status]);

  const breadcrumbs = [
    { active: true, path: "/", title: t("common.home") },
    {
      active: true,
      path: "/events/bookings",
      title: t("events_bookings.title"),
    },
    {
      active: false,
      title: details?.order_number
        ? `#${details.order_number}`
        : t("events_bookings.details"),
    },
  ];

  return (
    <>
      <Loader
        open={
          load?.loading &&
          (load?.type === "details" || load?.type === "update-status")
        }
      />
      <Box>
        <Stack
          direction="row"
          alignSelf="start"
          justifyContent="start"
          gap={2}
          width="100%"
          mb={2}
        >
          <Breadcrumb data={breadcrumbs} />
        </Stack>

        {details?.id ? (
          <BookingDetails
            details={details}
            canUpdate={canUpdate}
            statusOptions={statusOptions}
            onStatusChange={onStatusChange}
            statusDraft={statusDraft}
            setStatusDraft={setStatusDraft}
          />
        ) : (
          !load?.loading && (
            <Box sx={{ py: 6, textAlign: "center", color: "#8A8A8A" }}>
              {t("events_bookings.empty")}
            </Box>
          )
        )}
      </Box>
    </>
  );
}

export default BookingDetailsPage;
